const GUEST_KEY = 'ox_guest_id';
const CLAIMED_KEY = 'ox_guest_claimed';

/** Stable guest id for X-Ox-Guest (never share guest:anonymous). */
export function getOrCreateGuestId(): string {
  if (typeof window === 'undefined') return `ssr-${Date.now()}`;
  try {
    let id = localStorage.getItem(GUEST_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `g-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(GUEST_KEY, id);
    }
    return id;
  } catch {
    return `g-${Date.now()}`;
  }
}

/**
 * Prefer the Woo cookie guest id so JWT ownerRef, X-Ox-Guest, and claim-on-login share one identity.
 */
export function syncGuestId(preferred?: string | null): string {
  const cleaned = (preferred ?? '').trim().replace(/^guest:/, '');
  if (cleaned && typeof window !== 'undefined') {
    try {
      localStorage.setItem(GUEST_KEY, cleaned);
    } catch {
      /* ignore */
    }
    return cleaned;
  }
  return getOrCreateGuestId();
}

export function oxAuthHeaders(token?: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Ox-Guest': getOrCreateGuestId(),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  // Always send store from <ox-designer store-id> so asset/design fetches work
  // even when JWT is missing or was issued for a different tenant.
  if (typeof window !== 'undefined') {
    const host = window.document.querySelector('ox-designer');
    const storeId = host?.getAttribute('store-id')?.trim();
    if (storeId) headers['X-Ox-Store'] = storeId;
  }
  return headers;
}

/**
 * After login, transfer designs/assets from guest:{id} → woo:{user}.
 * Idempotent; skips if already claimed this guest for this owner in this browser.
 */
export async function claimGuestOwnership(
  apiBase: string,
  opts: {
    token: string;
    ownerRef: string;
    guestId?: string | null;
  },
): Promise<{ designs: number; assets: number } | null> {
  if (!opts.token || !opts.ownerRef || opts.ownerRef.startsWith('guest:')) {
    return null;
  }
  const guestId = syncGuestId(opts.guestId || getOrCreateGuestId());
  if (!guestId) return null;

  const claimKey = `${opts.ownerRef}:${guestId}`;
  try {
    if (sessionStorage.getItem(CLAIMED_KEY) === claimKey) return null;
  } catch {
    /* ignore */
  }

  const base = apiBase.replace(/\/$/, '');
  try {
    const res = await fetch(`${base}/ownership/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...oxAuthHeaders(opts.token),
      },
      body: JSON.stringify({ guestId }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { designs?: number; assets?: number };
    try {
      sessionStorage.setItem(CLAIMED_KEY, claimKey);
    } catch {
      /* ignore */
    }
    return {
      designs: data.designs ?? 0,
      assets: data.assets ?? 0,
    };
  } catch {
    return null;
  }
}
